import React, { useState } from 'react';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';
import { FlexRow, SvgIcon, TableData } from '@make-software/cspr-ui';
import { BodyText, Table, Tooltip } from '@block-explorer/ui';
import DetailsTableRow from '../details-table-row/details-table-row';
import DetailsNameTableData from '../details-name-table-data/details-name-table-data';
import DetailsValueTableData from '../details-value-table-data/details-value-table-data';
import {
  DetailsAccordionAssociatedKeysItem,
  TokenWrapper,
} from '../details-accordion-associated-keys/details-accordion-associated-keys-item';
import { InfoSvgIcon } from '../specialized-svg-icons/specialized-svg-icons';
import { useCreateAccountsInfoGetter, useGetV2Messages } from '../../hooks';
import { deriveHashListByKeysFromObject } from '../../utils/account';
import { useGetCasperNetwork } from '../../services/casper-network-context.tsx';

const MAXIMUM_VISIBLE_ROWS = 5;

const ArrowSvgIcon = styled(SvgIcon)(({ theme }) => ({
  path: {
    fill: theme.styleguideColors.contentBlue,
  },
}));

const CollapsedRow = styled(FlexRow)(({ theme }) => ({
  '&:hover': {
    span: {
      color: theme.styleguideColors.contentRed,
    },
    [`${ArrowSvgIcon}`]: {
      path: {
        fill: theme.styleguideColors.contentRed,
      },
    },
  },
}));

interface AssociatedKeysTableProps {
  loading: boolean;
  associatedKeys: { account_hash: string; weight: number }[];
  actionThresholds:
    | {
        deployment: number;
        key_management: number;
      }
    | undefined;
}

const AssociatedKeysTable = ({
  associatedKeys,
  actionThresholds,
  loading,
}: AssociatedKeysTableProps) => {
  const { t } = useTranslation();
  const { getLocalisationForNetworkVersion } = useGetCasperNetwork();
  const { associatedKeys: associatedKeysMessages } =
    getLocalisationForNetworkVersion;

  const [isCollapsed, setCollapsed] = useState<boolean>(true);

  const hashList = deriveHashListByKeysFromObject(associatedKeys, [
    'account_hash',
  ]);
  const { getAccountInfoByHash, loading: loadingAllAccountsInfo } =
    useCreateAccountsInfoGetter([], hashList);

  const sortedAssociatedKeys = associatedKeys.sort(
    (a, b) => b.weight - a.weight
  );

  const handleExpandList = () => {
    setCollapsed(!isCollapsed);
  };
  return (
    <Table
      paddingBottom={12}
      renderData={() => (
        <>
          <DetailsTableRow>
            <DetailsNameTableData>
              <Trans t={t}>Thresholds</Trans>
            </DetailsNameTableData>
            <DetailsValueTableData>
              <FlexRow itemsSpacing={19}>
                <TokenWrapper>
                  <FlexRow itemsSpacing={6} align="center">
                    <BodyText size={3} variation={'black'}>
                      {t(associatedKeysMessages.submission)}:
                    </BodyText>
                    <BodyText size={2} variation={'black'}>
                      {actionThresholds?.deployment}
                    </BodyText>
                    <Tooltip title={t(associatedKeysMessages.tooltip)}>
                      <InfoSvgIcon
                        src="assets/icons/ic-info.svg"
                        style={{ marginLeft: 6 }}
                      />
                    </Tooltip>
                  </FlexRow>
                </TokenWrapper>
                <TokenWrapper>
                  <FlexRow itemsSpacing={6} align="center">
                    <BodyText size={3} variation={'black'}>
                      <Trans t={t}>Key management:</Trans>
                    </BodyText>
                    <BodyText size={2} variation={'black'}>
                      {actionThresholds?.key_management}
                    </BodyText>
                    <Tooltip
                      title={t(
                        'Signature weight required to modify associated key settings'
                      )}
                    >
                      <InfoSvgIcon
                        src="assets/icons/ic-info.svg"
                        style={{ marginLeft: 6 }}
                      />
                    </Tooltip>
                  </FlexRow>
                </TokenWrapper>
              </FlexRow>
            </DetailsValueTableData>
          </DetailsTableRow>

          {sortedAssociatedKeys
            .filter((associatedKey, i) =>
              isCollapsed ? i < MAXIMUM_VISIBLE_ROWS : true
            )
            .map((associatedKey, index) => (
              <DetailsAccordionAssociatedKeysItem
                key={associatedKey.account_hash}
                data={associatedKey}
                index={index}
                loading={loading || loadingAllAccountsInfo}
                getAccountInfo={getAccountInfoByHash}
              />
            ))}
          {sortedAssociatedKeys.length > MAXIMUM_VISIBLE_ROWS && (
            <DetailsTableRow>
              <TableData>
                <CollapsedRow align="center" onClick={handleExpandList}>
                  <BodyText size={3} variation="blue">
                    {isCollapsed ? (
                      <>
                        <Trans t={t}>See all </Trans>
                        {associatedKeys?.length}
                        <Trans t={t}> keys</Trans>
                      </>
                    ) : (
                      <Trans t={t}>Hide keys </Trans>
                    )}
                  </BodyText>
                  <ArrowSvgIcon
                    src="assets/icons/ic-arrow-down.svg"
                    marginLeft
                    rotate={!isCollapsed}
                  />
                </CollapsedRow>
              </TableData>
            </DetailsTableRow>
          )}
        </>
      )}
    />
  );
};

export default AssociatedKeysTable;
